"use client";

import { useQuery } from "convex/react";
import { Link } from "react-router-dom";
import { api } from "../../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageTitle } from "@/lib/usePageTitle";
import { type SubscriptionTier, type SubscriptionStatus } from "@/lib/billing/plans";

function formatDate(ts: number | null | undefined) {
  if (!ts) return null;
  return new Date(ts).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function tierLabel(tier: SubscriptionTier) {
  switch (tier) {
    case "starter": return "Founder Starter";
    case "plus": return "Founder Plus";
    case "pro": return "Founder Pro";
    case "enterprise": return "Enterprise";
    default: return "Free";
  }
}

const FEATURES_LOST: Record<string, string[]> = {
  starter: [
    "Your 1 server organization and all its configurations",
    "Server listing in the BlockyNetworks Server Finder",
    "Plugin heartbeat monitoring",
    "Player account linking",
  ],
  plus: [
    "All 5 server organizations and their configurations",
    "Server listings in the BlockyNetworks Server Finder",
    "Plugin heartbeat monitoring across multiple servers",
    "Player account linking for all networks",
  ],
  pro: [
    "All 10 server organizations and their configurations",
    "Server listings in the BlockyNetworks Server Finder",
    "Plugin heartbeat monitoring across all servers",
    "Player account linking and priority queue access",
  ],
  enterprise: [
    "All enterprise organization seats",
    "Server listings in the BlockyNetworks Server Finder",
    "Priority queue access and advanced stats",
    "Plugin heartbeat monitoring across all servers",
  ],
};

function FeatureLostList({ tier }: { tier: SubscriptionTier }) {
  const features = FEATURES_LOST[tier] ?? [];
  if (!features.length) return null;

  return (
    <ul className="mt-3 space-y-2">
      {features.map((f) => (
        <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
          <svg className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          {f}
        </li>
      ))}
    </ul>
  );
}

export default function SubscriptionCancelledPage() {
  usePageTitle("Subscription Canceled");

  const summary = useQuery(api.billing.getMySummary);
  const isLoading = summary === undefined;

  const tier = (summary?.tier ?? "free") as SubscriptionTier;
  const status = (summary?.status ?? "none") as SubscriptionStatus;
  const accessEndsDate = formatDate(summary?.currentPeriodEnd ?? summary?.endsAt);
  const isCancelScheduled = summary?.cancelAtPeriodEnd === true;
  const isActive = status === "active" || status === "trialing";

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Confirmation banner */}
      <Card className="border-amber-300 bg-amber-50/70 dark:border-amber-700 dark:bg-amber-950/20">
        <CardHeader>
          <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/15">
            <svg className="h-5 w-5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <CardTitle className="text-amber-900 dark:text-amber-100">
            Cancellation Scheduled
          </CardTitle>
          <CardDescription className="text-amber-700 dark:text-amber-300">
            Your subscription has been set to cancel at the end of the current billing period.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-1 text-sm text-amber-800 dark:text-amber-200">
          {isLoading ? (
            <Skeleton className="h-4 w-56" />
          ) : (
            <>
              {accessEndsDate && isActive && (
                <p>
                  You retain full access until{" "}
                  <span className="font-semibold">{accessEndsDate}</span>.
                </p>
              )}
              <p>After that, your account moves to the free plan automatically.</p>
            </>
          )}
        </CardContent>
      </Card>

      {/* What you'll lose */}
      {!isLoading && tier !== "free" && (isCancelScheduled || isActive) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">What you&apos;ll lose access to</CardTitle>
            <CardDescription>
              These features will be unavailable when your {tierLabel(tier)} subscription ends
              {accessEndsDate ? ` on ${accessEndsDate}` : ""}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FeatureLostList tier={tier} />
            <p className="mt-4 text-sm text-muted-foreground">
              Your organizations will be archived (not deleted) for up to 30 days, giving you a
              window to re-subscribe and restore them instantly.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Re-subscribe nudge */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="text-base">Changed your mind?</CardTitle>
          <CardDescription>
            You can turn auto-renew back on any time before your access ends. Your subscription
            continues without interruption.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Link to="/dashboard/settings/billing">
            <Button>Restore Auto-Renew</Button>
          </Link>
          <Link to="/pricing">
            <Button variant="outline">View Plans</Button>
          </Link>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex flex-wrap gap-3">
        <Link to="/dashboard">
          <Button variant="outline">Return to Dashboard</Button>
        </Link>
        <Link to="/account/subscription">
          <Button variant="ghost">Subscription Details</Button>
        </Link>
      </div>
    </div>
  );
}
